// GoogleAuthService.js

/**
 * GoogleAuthService.js
 * 
 * Provides Google authentication services including sign-in and user node creation.
 */

// Firebase
import { auth } from 'firebaseConfig';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';

// Utilities
import { getDefaultNode } from 'utilities/NodeFactory';
import { generateSubstrings } from 'utilities/Strings';
import { getDefaultUser, buildUserSummary } from 'utilities/Users';

// Services
import { fetchCurrentUser } from 'services/users/UserService';

import { setNodeDoc } from 'services/nodes/NodeWriteService';

import { SIGNUPS_ENABLED, SIGNUPS_PAUSED_MESSAGE } from 'login/signupAvailability';

/**
 * Signs in with Google via popup and ensures a USER node exists at `_nodes/{uid}`.
 *
 * @returns {Promise<{ user: object, authUser: import('firebase/auth').User, isNewUser: boolean, email: string }>}
 */
export async function signInWithGoogleAndEnsureUserNode() {

  // Start Google sign-in flow.
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  // Sign in with popup.
  const userCredential = await signInWithPopup(auth, provider);

  // Extract auth user and UID.
  const authUser = userCredential.user;
  const uid = authUser.uid;

  // Encourage Firestore to pick up a fresh auth token immediately.
  try {
    await authUser.getIdToken(true);
  } catch (e) {
    console.warn('[auth] getIdToken failed (non-fatal)', e);
  }

  // If this user already has a profile node, use it.
  let existingUser = null;
  try {
    existingUser = await fetchCurrentUser(authUser);
  } catch (e) {
    // Can happen transiently right after popup auth; treat as new-user path.
    console.warn('[auth] fetchCurrentUser failed (continuing as new user)', e);
  }

  // Existing user: return combined data.
  if (existingUser) {
    // Normalize existing email.
    const existingEmail = String(existingUser?.data_profile?.email || authUser.email || '').trim().toLowerCase();

    // Ensure appointment access is claimed (best-effort).
    return { 
      user: existingUser, 
      authUser, 
      isNewUser: false, 
      email: existingEmail 
    };
  }

  // Public sign-up paused: do not provision new Google accounts.
  if (!SIGNUPS_ENABLED) {
    try {
      await authUser.delete();
    } catch (e) {
      console.warn('[auth] failed to delete paused Google signup user', e);
      try {
        await auth.signOut();
      } catch (signOutError) {
        console.warn('[auth] failed to sign out paused Google signup user', signOutError);
      }
    }
    throw new Error(SIGNUPS_PAUSED_MESSAGE);
  }

  // New user: create their USER node (mirrors email signup shape).
  const email = String(authUser.email || '').trim().toLowerCase();

  // Email is required for Google sign-in users.
  if (!email) {
    throw new Error('Google sign-in did not provide an email address.');
  }

  // Derive display name.
  const name = String(authUser.displayName || '').trim() || email.split('@')[0];

  // Create default profile data.
  const profile = getDefaultUser(uid, name, email);

  // Create default USER node.
  const defaultNode = await getDefaultNode('USER');

  // Generate search index.
  const index = generateSubstrings(name.toLowerCase(), 5);

  // Build profile node.
  const profileNode = {
    ...defaultNode,
    type: 'USER',
    key: uid,
    userKey: uid,
    title: name,
    emailVerified: authUser.emailVerified === true,
    user: buildUserSummary({ data_profile: profile }),
    data_profile: profile,
    index,
  };

  // Deep clone to ensure no unexpected fields.
  const safePayload = JSON.parse(JSON.stringify(profileNode));

  // Write USER node to Firestore.
  await setNodeDoc(uid, safePayload, { merge: false });

  const user = {
    key: uid,
    email: authUser.email,
    displayName: authUser.displayName,
    emailVerified: authUser.emailVerified,
    phoneNumber: authUser.phoneNumber,
    userKey: uid,
    ...safePayload,
  };

  return { user, authUser, isNewUser: true, email };
}
